// Bar submission validation utilities
import { PROFANITY_LIST } from '../lib/contentFilter';
import { getMetroById } from '../data/metros';
import { getDistanceMiles } from './geo';

export { PROFANITY_LIST };

const DEFAULT_METRO = getMetroById('chicago');
export const SUBMIT_MAX_DISTANCE_MILES = 50;

export { getDistanceMiles };

/**
 * Check if a name contains at least one vowel
 */
function hasVowel(str) {
  return /[aeiouAEIOU]/.test(str);
}

/**
 * Check if a string is all the same character repeated
 */
function isRepeatedCharacter(str) {
  if (str.length < 3) return false;
  const firstChar = str[0].toLowerCase();
  return str.toLowerCase().split('').every(c => c === firstChar);
}

/**
 * Check if a string is only numbers
 */
function isOnlyNumbers(str) {
  return /^\d+$/.test(str.trim());
}

/**
 * Check if bar name contains banned words
 */
function containsBannedWord(name) {
  const lowerName = name.toLowerCase();
  for (const word of PROFANITY_LIST) {
    if (lowerName.includes(word)) {
      return true;
    }
  }
  return false;
}

/**
 * Validate bar submission for auto-approval
 * @param {Object} barData - The bar submission data
 * @param {Object} options - Additional validation options
 * @returns {Object} { approved: boolean, reason: string|null }
 */
export function validateBarSubmission(barData, options = {}) {
  const { coordinates, recentSubmissionCount = 0 } = options;
  
  // 1. Check required fields
  if (!barData.name || !barData.name.trim()) {
    return { approved: false, reason: 'Please fill in all required fields' };
  }
  if (!barData.address || !barData.address.trim()) {
    return { approved: false, reason: 'Please fill in all required fields' };
  }
  if (!barData.neighborhood) {
    return { approved: false, reason: 'Please fill in all required fields' };
  }
  
  const trimmedName = barData.name.trim();
  
  // 2. Check for obviously fake bar name
  // Less than 3 characters
  if (trimmedName.length < 3) {
    return { approved: false, reason: 'Please enter a valid bar name' };
  }
  
  // Only numbers
  if (isOnlyNumbers(trimmedName)) {
    return { approved: false, reason: 'Please enter a valid bar name' };
  }
  
  // No vowels (random characters like "asdflkj" would fail this - wait no, that has 'a')
  // Let's check for consonant-only strings that are 4+ chars
  if (trimmedName.length >= 4 && !hasVowel(trimmedName)) {
    return { approved: false, reason: 'Please enter a valid bar name' };
  }
  
  // All same character repeated
  if (isRepeatedCharacter(trimmedName)) {
    return { approved: false, reason: 'Please enter a valid bar name' };
  }
  
  // 3. Check for banned words
  if (containsBannedWord(trimmedName)) {
    return { approved: false, reason: 'Please enter a valid bar name' };
  }
  
  // 4. Allow suburbs/exurbs of the selected metro; reject wrong-coast spam
  if (coordinates && coordinates.lat && coordinates.lng) {
    const metro = options.metro || getMetroById(barData.metroId) || DEFAULT_METRO;
    const distance = getDistanceMiles(
      coordinates.lat,
      coordinates.lng,
      metro.center.lat,
      metro.center.lng
    );
    if (distance > SUBMIT_MAX_DISTANCE_MILES) {
      return {
        approved: false,
        reason: `This location is too far from ${metro.name}. Pick a closer city or include the suburb in the address.`
      };
    }
  }
  
  // 5. Rate limiting check
  if (recentSubmissionCount >= 5) {
    return { approved: false, reason: 'Slow down! Please wait a few minutes before submitting another bar' };
  }
  
  // All checks passed - approve!
  return { approved: true, reason: null };
}

/**
 * Check if submission should be silently rejected (spam)
 * These submissions don't need admin notification
 */
export function isSpamSubmission(barData) {
  const name = barData.name?.trim() || '';
  
  // Very short names
  if (name.length < 3) return true;
  
  // Only numbers
  if (isOnlyNumbers(name)) return true;
  
  // All same character
  if (isRepeatedCharacter(name)) return true;
  
  // Contains profanity
  if (containsBannedWord(name)) return true;
  
  return false;
}

